/**
 * Clients / CRM domain types.
 *
 * Talbert serves architects, GCs, dealers, and direct end-users
 * across the Western U.S., Canada, and Mexico. The schema is built
 * around three entities:
 *
 *   - Account     — the firm or company (Gensler, HOK, etc.)
 *   - Contact     — a person at that firm
 *   - Interaction — call, email, meeting, site visit, spec handoff
 *
 * Pipeline stage tracks where the relationship is in the sales cycle.
 */

export type ClientType = 'architect' | 'gc' | 'dealer' | 'developer' | 'end_user' | 'other';

export const CLIENT_TYPE_LABELS: Record<ClientType, string> = {
  architect: 'Architect',
  gc:        'General Contractor',
  dealer:    'Dealer',
  developer: 'Developer',
  end_user:  'End User',
  other:     'Other',
};

export type PipelineStage =
  | 'lead'
  | 'qualified'
  | 'specified'
  | 'quoted'
  | 'awarded'
  | 'closed_won'
  | 'closed_lost'
  | 'dormant';

export const PIPELINE_STAGES: PipelineStage[] = [
  'lead', 'qualified', 'specified', 'quoted', 'awarded', 'closed_won', 'closed_lost', 'dormant',
];

export const PIPELINE_LABELS: Record<PipelineStage, string> = {
  lead:         'Lead',
  qualified:    'Qualified',
  specified:    'Specified',
  quoted:       'Quoted',
  awarded:      'Awarded',
  closed_won:   'Closed · Won',
  closed_lost:  'Closed · Lost',
  dormant:      'Dormant',
};

/** Pipeline stages that count as "active opportunities" */
export const ACTIVE_STAGES: PipelineStage[] = ['lead', 'qualified', 'specified', 'quoted', 'awarded'];

export type Address = {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
};

export type Client = {
  id: string;
  name: string;
  type: ClientType;
  stage: PipelineStage;
  website?: string;
  phone?: string;
  email?: string;
  address?: Address;
  territory?: string;          // 'Western US', 'Canada', 'Mexico', 'Pacific NW', etc.
  ownerUid?: string;           // sales rep responsible
  ownerName?: string;
  parentClientId?: string;     // for branch offices under a parent firm
  estimatedAnnualValue?: number;
  notes?: string;
  tags?: string[];
  createdAt?: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  lastInteractionAt?: string;
};

export type Contact = {
  id: string;
  clientId: string;
  firstName: string;
  lastName: string;
  title?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  isPrimary?: boolean;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type InteractionType = 'call' | 'email' | 'meeting' | 'site_visit' | 'spec_handoff' | 'note';

export const INTERACTION_LABELS: Record<InteractionType, string> = {
  call:         'Call',
  email:        'Email',
  meeting:      'Meeting',
  site_visit:   'Site Visit',
  spec_handoff: 'Spec Handoff',
  note:         'Note',
};

export type Interaction = {
  id: string;
  clientId: string;
  contactId?: string;
  type: InteractionType;
  subject: string;
  body?: string;
  occurredAt: string;
  authorUid: string;
  authorName?: string;
  createdAt?: string;
};
